/**
 * A run's outcomes: what it produced, recorded as `outcome.recorded` events. This module is
 * the shared contract the api, the loop host and the run page use — the fold from a run's
 * log to its outcomes, the storage key layout, the path and size rules for an outcome's
 * files, and the payloads of the host's upload rpc and a member's file url rpc.
 *
 * **Latest record wins.** An outcome may be recorded again with a new status or files; its
 * latest record is the outcome, kept in the order its id was first recorded.
 *
 * Instants on the wire are `Schema.DateTimeUtc` (ISO-8601 strings encoded). Optional fields
 * are absent, never null.
 *
 * @since 1.0.0
 */
import { sha256 } from "@noble/hashes/sha2";
import { bytesToHex } from "@noble/hashes/utils";
import { Option, Schema } from "effect";
import { RunRef } from "./api.ts";
import { CheckPresignedUrl } from "./checks.ts";
import type { Outcome, RunEvent } from "./model.ts";
import { OutcomeFile } from "./model.ts";

// ============================================================================
// Limits and storage layout
// ============================================================================

/**
 * The archive bucket's prefix for outcome files.
 *
 * @since 1.0.0
 */
export const OUTCOME_KEY_PREFIX = "v3-outcomes";

/**
 * How long a presigned outcome url stays valid, in seconds.
 *
 * @since 1.0.0
 */
export const OUTCOME_PRESIGN_SECONDS = 900;

/**
 * The largest file an outcome may store, in bytes.
 *
 * @since 1.0.0
 */
export const OUTCOME_FILE_MAX_BYTES = 50 * 1024 * 1024;

/**
 * The most files one outcome may store.
 *
 * @since 1.0.0
 */
export const OUTCOME_FILES_MAX = 20;

/**
 * The outcome kinds the run page knows how to present. `Outcome.kind` stays open: an
 * unknown kind is shown generically.
 *
 * @since 1.0.0
 */
export const KNOWN_OUTCOME_KINDS = [
  "change_request",
  "commit",
  "design",
  "document",
  "issue_set",
  "environment",
  "test_run",
  "file_set",
] as const;
export type KnownOutcomeKind = (typeof KNOWN_OUTCOME_KINDS)[number];

const PATH_MAX_CHARS = 512;
const PATH_MAX_SEGMENTS = 16;
const SEGMENT = /^[A-Za-z0-9._+-]{1,128}$/;
const ULID = /^[0-9A-HJKMNP-TV-Z]{26}$/;

/**
 * Whether `path` may name an outcome file: a relative path of 1–16 `/`-separated segments,
 * each 1–128 characters from `[A-Za-z0-9._+-]` and never `.` or `..`, at most 512
 * characters in all.
 *
 * @since 1.0.0
 */
export const isOutcomePath = (path: string): boolean => {
  const segments = path.split("/");
  return path.length <= PATH_MAX_CHARS
    && segments.length <= PATH_MAX_SEGMENTS
    && segments.every((segment) => SEGMENT.test(segment) && segment !== "." && segment !== "..");
};

/**
 * Whether `id` is a ulid, as an outcome's id must be.
 *
 * @since 1.0.0
 */
export const isOutcomeId = (id: string): boolean => ULID.test(id);

/**
 * Where an outcome's file lives in the archive bucket.
 *
 * @since 1.0.0
 */
export const outcomeFileKey = (customerId: string, runId: string, outcomeId: string, path: string): string =>
  `${OUTCOME_KEY_PREFIX}/${customerId}/${runId}/${outcomeId}/${path}`;

// ============================================================================
// Content type and disposition
// ============================================================================

const CONTENT_TYPES: ReadonlyMap<string, string> = new Map([
  ["md", "text/markdown"],
  ["txt", "text/plain"],
  ["log", "text/plain"],
  ["patch", "text/x-diff"],
  ["diff", "text/x-diff"],
  ["json", "application/json"],
  ["csv", "text/csv"],
  ["html", "text/html"],
  ["pdf", "application/pdf"],
  ["png", "image/png"],
  ["jpg", "image/jpeg"],
  ["jpeg", "image/jpeg"],
  ["gif", "image/gif"],
  ["webp", "image/webp"],
  ["svg", "image/svg+xml"],
  ["mp4", "video/mp4"],
  ["webm", "video/webm"],
  ["zip", "application/zip"],
]);

const OCTET_STREAM = "application/octet-stream";

/** The types a browser may render in place; HTML and SVG are left out, since they run script. */
const INLINE_TYPES: ReadonlySet<string> = new Set([
  "text/plain",
  "text/markdown",
  "text/x-diff",
  "text/csv",
  "application/json",
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "video/mp4",
  "video/webm",
]);

const lastSegment = (path: string): string => path.slice(path.lastIndexOf("/") + 1);

/**
 * The content type of a file by its extension, case-insensitive; `application/octet-stream`
 * for an unknown one or none.
 *
 * @since 1.0.0
 */
export const contentTypeOf = (path: string): string => {
  const name = lastSegment(path);
  const dot = name.lastIndexOf(".");
  return dot < 0 ? OCTET_STREAM : CONTENT_TYPES.get(name.slice(dot + 1).toLowerCase()) ?? OCTET_STREAM;
};

/**
 * The `Content-Disposition` to serve an outcome file with: `inline` only for a type a
 * browser renders safely and when not asked to download, `attachment` otherwise.
 *
 * @since 1.0.0
 */
export const outcomeDisposition = (file: OutcomeFile, download: boolean): string =>
  `${!download && INLINE_TYPES.has(file.contentType) ? "inline" : "attachment"}; filename="${
    lastSegment(file.path).replace(/["\\]/g, "_")
  }"`;

/**
 * The lowercase hex SHA-256 of `bytes`, as an outcome file's `sha256` records it.
 *
 * @since 1.0.0
 */
export const sha256Hex = (bytes: Uint8Array): string => bytesToHex(sha256(bytes));

// ============================================================================
// The fold
// ============================================================================

/**
 * A run's outcomes: the latest record of each outcome id, in the order each was first
 * recorded. Other event kinds are ignored.
 *
 * @since 1.0.0
 */
export const outcomesOf = (events: ReadonlyArray<RunEvent>): ReadonlyArray<Outcome> =>
  Array.from(
    events.reduce(
      (latest, e) => (e.kind === "outcome.recorded" ? latest.set(e.outcome.id, e.outcome) : latest),
      new Map<string, Outcome>(),
    ).values(),
  );

/**
 * An outcome's file by path, looked up in that outcome's latest record only: a file a later
 * record dropped is gone.
 *
 * @since 1.0.0
 */
export const findOutcomeFile = (
  events: ReadonlyArray<RunEvent>,
  outcomeId: string,
  path: string,
): Option.Option<{ readonly outcome: Outcome; readonly file: OutcomeFile }> =>
  Option.fromNullable(outcomesOf(events).find((outcome) => outcome.id === outcomeId)).pipe(
    Option.flatMap((outcome) =>
      Option.fromNullable(outcome.files.find((file) => file.path === path)).pipe(
        Option.map((file) => ({ outcome, file })),
      )
    ),
  );

// ============================================================================
// Rpc payloads
// ============================================================================

/**
 * The host's request for a presigned url to upload one outcome file to.
 *
 * @since 1.0.0
 */
export const OutcomeUploadInput = Schema.Struct({
  runId: Schema.String,
  outcomeId: Schema.String,
  path: Schema.String,
  bytes: Schema.Int.pipe(Schema.greaterThanOrEqualTo(0)),
});
export type OutcomeUploadInput = typeof OutcomeUploadInput.Type;

/**
 * Where the host uploads an outcome file: its archive key and a presigned `PUT`.
 *
 * @since 1.0.0
 */
export const OutcomeUpload = Schema.Struct({
  key: Schema.String,
  upload: CheckPresignedUrl,
});
export type OutcomeUpload = typeof OutcomeUpload.Type;

/**
 * A member's request for a url to one outcome file; `download` forces `attachment`.
 *
 * @since 1.0.0
 */
export const OutcomeFileUrlInput = Schema.Struct({
  ...RunRef.fields,
  outcomeId: Schema.String,
  path: Schema.String,
  download: Schema.optional(Schema.Boolean),
});
export type OutcomeFileUrlInput = typeof OutcomeFileUrlInput.Type;

/**
 * A presigned `GET` for an outcome file, with the file's record.
 *
 * @since 1.0.0
 */
export const OutcomeFileUrl = Schema.Struct({
  url: Schema.String,
  expiresAt: Schema.DateTimeUtc,
  file: OutcomeFile,
});
export type OutcomeFileUrl = typeof OutcomeFileUrl.Type;

/**
 * Presigning an outcome file's url failed.
 *
 * @since 1.0.0
 */
export class OutcomeStorageError extends Schema.TaggedError<OutcomeStorageError>()("OutcomeStorageError", {
  operation: Schema.Literal("presign"),
  message: Schema.String,
}) {}
